import type React from 'react';
import type { StorefrontProduct } from '../../api/client';
import { isThemePreview } from '../../api/previewVisit';
import { useCommerce, type Commerce } from '../../commerce/ShopCommerce';
import { useElementRender } from './elementRenderContext';
import { textProp, type ElementProps } from './elementProps';
import { useProduct, useProductVariant } from './productContext';
import { orderable } from '../../commerce/variantStock';
import { inStoreLanguage, shopWords } from '../../commerce/shopWords';

// What buying the product in scope stands on, shared by the buy box and the sticky bar under it: the
// variant and quantity chosen, whether it can be ordered, the words its button says, and the dropdown a
// product with several variants is chosen in. Both draw the same choice, so picking in one moves the other.

/** One variant of a product. */
export type Variant = StorefrontProduct['variants'][number];

/** The product in scope, the variant and quantity chosen, and whether it can be ordered right now. */
export type Purchase = {
  product: StorefrontProduct | null;
  variant: Variant | undefined;
  quantity: number;
  setQuantity: (quantity: number) => void;
  sample: boolean;
  /** True in the builder and a theme preview, where nothing may be ordered. */
  locked: boolean;
  available: boolean;
  select: (variantId: string) => void;
  commerce: Commerce | null;
};

// Reads the product in scope, its chosen variant and quantity, and whether it can be ordered now.
export function usePurchase(): Purchase {
  const { editing } = useElementRender();
  const product = useProduct() ?? null;
  const commerce = useCommerce() ?? null;
  // The quantity belongs to the product scope, so an order form beside the buy box orders the same number.
  const { selected, select, quantity, setQuantity } = useProductVariant();
  const variant = product?.variants.find((row) => row.id === selected) ?? product?.variants[0];
  const sample = Boolean(product?.id.startsWith('sample-'));
  const available = product?.status === 'ACTIVE' && orderable(variant, quantity) && !sample;
  return { product, variant, quantity, setQuantity, sample, locked: editing || isThemePreview(), available, select, commerce };
}

/** How a buy button is drawn: greyed out, or live-looking but inert with a note on why it does nothing. */
export type BuyButtonState = { disabled: boolean; 'aria-disabled'?: true; title?: string };

/** What a locked buy button says when hovered. */
const LOCKED_NOTE = 'Buying is off in the preview';

// Draws a buy button live in the builder and a preview, where it does nothing, and greyed out only when it cannot order.
export const buyButton = (locked: boolean, orderable: boolean): BuyButtonState =>
  (locked ? { disabled: false, 'aria-disabled': true, title: LOCKED_NOTE } : { disabled: !orderable });

// Words the main button: choose options, sold out, or the merchant's label — a sample reads as a real product would.
export function buttonLabel(buy: Purchase, props: ElementProps, choosing: boolean): string {
  const words = shopWords(buy.commerce?.language);
  if (choosing) return words.chooseOptions;
  return buy.available || buy.sample ? inStoreLanguage(textProp(props, 'label', ''), shopWords('en').addToCart, words.addToCart) : words.soldOut;
}

// Draws the variants as one dropdown, the way the theme has always offered them.
export const VariantSelect: React.FC<{ buy: Purchase; disabled: boolean }> = ({ buy, disabled }) => (
  <label className="xe-purchase-field"><span>{shopWords(buy.commerce?.language).chooseAnOption}</span><span className="xe-purchase-select">
    <select value={buy.variant?.id ?? ''} disabled={disabled} onChange={(event) => buy.select(event.target.value)}>
      {buy.product?.variants.map((row) => <option key={row.id} value={row.id} disabled={!orderable(row) && !buy.sample}>{row.title}</option>)}
    </select>
  </span></label>
);
